import type {ReactElement} from 'react';
import React, {useRef, useState} from 'react';
import {PlayPauseButton} from '@Components/media-control-buttons/components/play-pause-button';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {ControlledSlider} from '@Components/controlled-slider';
import {Text, TextSize} from '@Components/text';
import {convertSecondsToMMSSFormat} from '@Utils/date.util';
import {ClickableDiv} from '@Components/clickable-div';
import LoadingPanel from '@Panels/loading-panel/loading-panel';
import {LOADER_SIZE} from '@Components/circular-progress-loader';
import {isIOS} from 'react-device-detect';
import {useAppSelector} from '@/hooks';
import styles from './video-playback.module.scss';

interface VideoPlaybackProps {
  duration: number;
  isOnMobile?: boolean;
  width: number;
  showIosPlaybackVideoPreloader: boolean;
  setShowIosPlaybackVideoPreloader?(show: boolean): void;
}

const PLAYBACK_VIDEO_SEEKBAR_WIDTH_CORRECTION = 160;

export function VideoPlayback({isOnMobile = false, ...props}: VideoPlaybackProps): ReactElement | null {
  const [isPlaybackVideoPlaying, setIsPlaybackVideoPlaying] = useState(isIOS);
  const [videoPlaybackSeekTime, setVideoPlaybackSeekTime] = useState(0);
  const playbackVideoRef = useRef<HTMLVideoElement | null>(null);

  const [showSeekbar, setShowSeekbar] = useState(true);
  const [initialDesktopOverlayShown, setInitialDesktopOverlayShown] = useState(false);

  const mediaUrl = useAppSelector((state) => {
    return state.recordImageAndVideoPanel.mediaUrl;
  });

  const selectedCamera = useAppSelector((state) => {
    return state.recordImageAndVideoPanel.selectedCamera;
  });

  const handlePlaybackTimeUpdate = (): void => {
    if (playbackVideoRef.current) {
      setVideoPlaybackSeekTime(playbackVideoRef.current.currentTime);
    }
  };

  const onPlaybackVideoSeeked = (seek: number): void => {
    setVideoPlaybackSeekTime(seek);

    if (playbackVideoRef.current) {
      playbackVideoRef.current.currentTime = seek;
    }
  };

  const pausePlaybackVideo = (): void => {
    if (playbackVideoRef.current) {
      playbackVideoRef.current.pause();
      setIsPlaybackVideoPlaying(false);
      setShowSeekbar(true);
    }
  };

  const playPlaybackVideo = (): void => {
    if (playbackVideoRef.current) {
      void playbackVideoRef.current.play();
      setIsPlaybackVideoPlaying(true);
    }
  };

  const togglePlaybackVideoPlayingState = (): void => {
    if (isPlaybackVideoPlaying) {
      pausePlaybackVideo();
    } else {
      playPlaybackVideo();
    }
  };

  const getVideoPlaybackForMobile = (): ReactElement => {
    return (
      <ClickableDiv onClick={togglePlaybackVideoPlayingState} className="_full-height _full-width">
        <div className={`_full-width ${styles.seekbarOverlayWrapperMobile}`}>
          <div className={`spacing-p-l-2 spacing-p-r-2 spacing-p-b-1 _full-width flexbox ${styles.seekbar}`}>
            <PlayPauseButton
              iconType={IconType.SHADOW}
              iconShape={IconShape.CIRCLE}
              isPlaying={isPlaybackVideoPlaying}
              onPause={pausePlaybackVideo}
              onPlay={playPlaybackVideo}
              iconSize={IconSize.SIZE_ICON_16}
            />

            <ClickableDiv
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="flex-center"
              style={{width: `${props.width - PLAYBACK_VIDEO_SEEKBAR_WIDTH_CORRECTION}px`}}
            >
              <ControlledSlider
                addDelayToChange
                step={0.01}
                min={0}
                max={props.duration}
                className={`_full-width ${styles.slider}`}
                value={videoPlaybackSeekTime}
                onSlide={onPlaybackVideoSeeked} // TODO: i think the onClick for  the parent ClickableDiv is also fired here (play/pause state changed on video seek)
              />
            </ClickableDiv>

            <div className={styles.timeWrapper}>
              <Text val={`${convertSecondsToMMSSFormat(videoPlaybackSeekTime)} / `} size={TextSize.XXSMALL} />
              <Text val={convertSecondsToMMSSFormat(props.duration)} size={TextSize.XXSMALL} />
            </div>
          </div>
        </div>
        <video
          onLoadedMetadata={() => {
            if (playbackVideoRef.current) {
              // CodeReviewTaimurDiscuss: cleanup for this
              // we should optimise this maybe, it will be fired a lot of times if the user drags the seekbar for this video
              // can add delay to controlled slider if it is being dragged manually
              playbackVideoRef.current.addEventListener('timeupdate', handlePlaybackTimeUpdate);
              playbackVideoRef.current.addEventListener('ended', () => {
                setIsPlaybackVideoPlaying(false);

                if (!isOnMobile) {
                  setShowSeekbar(true);
                }
              });
            }
          }}
          onPlay={() => {
            playPlaybackVideo();
          }}
          ref={playbackVideoRef}
          playsInline
          autoPlay={isIOS}
          className={`_full-width _full-height ${styles.contain} ${selectedCamera !== 'environment' ? styles.mirror : ''} ${isOnMobile ? '' : 'radius-4'}`}
          src={`${mediaUrl}`}
        />
      </ClickableDiv>
    );
  };

  const getPlaybackVideoSeekbarForDesktop = (): ReactElement => {
    return (
      <div className={`_full-width radius-8 ${styles.seekbarOverlayWrapper} ${showSeekbar ? styles.show : ''}`}>
        <div className={`spacing-p-l-2 spacing-p-r-2 _full-width flexbox ${styles.seekbar}`}>
          <PlayPauseButton
            className={styles.playPauseButton}
            iconType={IconType.DARK}
            iconShape={IconShape.CIRCLE}
            isPlaying={isPlaybackVideoPlaying}
            onPause={pausePlaybackVideo}
            onPlay={playPlaybackVideo}
            iconSize={IconSize.SIZE_ICON_16}
          />
          <ClickableDiv
            className={`flex-center `}
            style={{width: `${props.width - PLAYBACK_VIDEO_SEEKBAR_WIDTH_CORRECTION}px`}}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <ControlledSlider
              addDelayToChange
              step={0.01}
              min={0}
              max={props.duration}
              className={`_full-width ${styles.slider}`}
              value={videoPlaybackSeekTime}
              onSlide={onPlaybackVideoSeeked}
            />
          </ClickableDiv>
          <div className={styles.timeWrapper}>
            <Text val={`${convertSecondsToMMSSFormat(videoPlaybackSeekTime)} / `} size={TextSize.XXSMALL} />
            <Text val={convertSecondsToMMSSFormat(props.duration)} size={TextSize.XXSMALL} />
          </div>
        </div>
      </div>
    );
  };

  const getInitialPlayOverlayForDesktop = (): ReactElement => {
    return (
      <ClickableDiv
        onClick={() => {
          playPlaybackVideo();
          setInitialDesktopOverlayShown(true);
        }}
        className={`_full-height _full-width flex-center ${styles.initialPlayOverlayWrapperDesktop}`}
      >
        <div className={`_full-height _full-width flex-center ${styles.initialPlayOverlayDesktop}`} />
        <PlayPauseButton
          className={styles.playPauseButton}
          iconType={IconType.GHOST}
          iconShape={IconShape.CIRCLE}
          isPlaying={isPlaybackVideoPlaying}
          onPause={pausePlaybackVideo}
          onPlay={playPlaybackVideo}
          iconSize={IconSize.SIZE_ICON_32}
        />
      </ClickableDiv>
    );
  };

  const getVideoPlaybackForDesktop = (): ReactElement => {
    return (
      <ClickableDiv
        onMouseEnter={() => {
          return setShowSeekbar(true);
        }}
        onMouseLeave={() => {
          if (!isPlaybackVideoPlaying) {
            return;
          }
          setShowSeekbar(false);
        }}
        onClick={togglePlaybackVideoPlayingState}
        className={`flex-center ${styles.videoFrame}`}
      >
        {initialDesktopOverlayShown ? getPlaybackVideoSeekbarForDesktop() : getInitialPlayOverlayForDesktop()}
        <video
          onLoadedMetadata={() => {
            if (playbackVideoRef.current) {
              // CodeReviewTaimurDiscuss: cleanup for this
              // we should optimise this maybe, it will be fired a lot of times if the user drags the seekbar for this video
              // can add delay to controlled slider if it is being dragged manually
              playbackVideoRef.current.addEventListener('timeupdate', handlePlaybackTimeUpdate);
              playbackVideoRef.current.addEventListener('ended', () => {
                setIsPlaybackVideoPlaying(false);

                if (!isOnMobile) {
                  setShowSeekbar(true);
                }
              });
            }
          }}
          ref={playbackVideoRef}
          playsInline
          className={`_full-width ${styles.videoPlayback} ${styles.mirror} ${isOnMobile ? '' : 'radius-4'}`}
          src={`${mediaUrl}`}
        />
      </ClickableDiv>
    );
  };

  const getVideoPlayback = (): ReactElement => {
    if (isOnMobile) {
      if (props.showIosPlaybackVideoPreloader) {
        setTimeout(() => {
          if (props.setShowIosPlaybackVideoPreloader) {
            props.setShowIosPlaybackVideoPreloader(false);
          }
        }, 2000);

        return <LoadingPanel loaderSize={LOADER_SIZE.LARGE} />;
      }
      return getVideoPlaybackForMobile();
    }
    return getVideoPlaybackForDesktop();
  };

  return getVideoPlayback();
}
